// Libraries
import React from 'react';

// Supermove
import {gql} from '@supermove/graphql';
import {useToast} from '@supermove/hooks';
import {AccountGrantModel} from '@supermove/models';

// App
import CautionModal from '@shared/design/components/Modal/SmallModal/CautionModal';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import UpdateAccountGrantForm from '@shared/modules/AccountGrant/forms/UpdateAccountGrantForm';
import useUpdateAccountGrantMutation from '@shared/modules/AccountGrant/hooks/useUpdateAccountGrantMutation';

const SetAsPrimaryAccountModal = ({
  isOpen,
  handleClose,
  currentPrimaryAccount,
  newPrimaryAccount,
}: {
  isOpen: boolean;
  handleClose: () => void;
  currentPrimaryAccount?: string;
  newPrimaryAccount: AccountGrantModel;
}) => {
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: `${newPrimaryAccount.identifier} set as primary account`,
  });
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: `Error setting ${newPrimaryAccount.identifier} as primary account`,
  });
  const {form: updateAccountGrantForm, handleSubmit: updateAccountGrant} =
    useUpdateAccountGrantMutation({
      updateAccountGrantForm: UpdateAccountGrantForm.edit({
        accountGrantId: newPrimaryAccount.id,
        isPrimary: newPrimaryAccount.isPrimary,
      }),
      onSuccess: () => {},
      refetchQueries: [
        'OrganizationSettingsCommunicationAccountsTable',
        'CommunicationAccountsPage',
      ],
      onError: (errors) => console.log({errors}),
    });

  const handleSetAsPrimaryAccount = async () => {
    const result = updateAccountGrant({
      variables: {
        accountGrantForm: UpdateAccountGrantForm.toMutation({
          ...updateAccountGrantForm.values.updateAccountGrantForm,
          isPrimary: true,
        }),
      },
    });
    handleClose();

    // onSuccess is not being called, so do it here
    try {
      const response = await result;
      if (response?.data?.response?.accountGrant) {
        successToast.handleToast();
      } else {
        errorToast.handleToast();
      }
    } catch (error) {
      errorToast.handleToast();
    }
  };

  return (
    <CautionModal
      isOpen={isOpen}
      handlePressOutside={handleClose}
      title={'Set as Primary Account?'}
      message={
        currentPrimaryAccount
          ? `Setting this account as primary will replace ${currentPrimaryAccount}`
          : `Setting this account as primary will make it the default account for all branches.`
      }
      handlePrimaryAction={handleSetAsPrimaryAccount}
      primaryActionText={'Confirm'}
      handleSecondaryAction={handleClose}
      secondaryActionText={'Cancel'}
    />
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SetAsPrimaryAccountModal.fragment = gql`
  fragment SetAsPrimaryAccountModal on AccountGrant {
    id
    isPrimary
  }
`;

export default SetAsPrimaryAccountModal;
